import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { isSignInWithEmailLink, onAuthStateChanged, updatePassword, type User } from 'firebase/auth';
import { auth } from './firebase';
import './styles/App.css';
import Navbar from './components/Navbar';
import Hero from './components/Hero';
import LogosBar from './components/LogosBar';
import DecisionSection from './components/DecisionSection';
import WhatsIncluded from './components/WhatsIncluded';
import MultiMarketComparison from './components/MultiMarketComparison';
import WhySimulation from './components/WhySimulation';
import HowWeBuild from './components/HowWeBuild';
import GlobalCoverage from './components/GlobalCoverage';
import FAQ from './components/FAQ';
import BottomCTA from './components/BottomCTA';
import Footer from './components/Footer';
import FounderPage from './pages/FounderPage';
import ExecutivePage from './pages/ExecutivePage';
import InvestorPage from './pages/InvestorPage';
import ComparisonPage from './pages/ComparisonPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import IntakePage from './pages/IntakePage';
import DashboardPage from './pages/DashboardPage';
import SetupPage from './pages/SetupPage';
import LoginModal from './components/LoginModal';
import IntakeModal from './components/IntakeModal';

export type PageType =
  | 'home'
  | 'founder'
  | 'executive'
  | 'investor'
  | 'comparison'
  | 'about'
  | 'contact'
  | 'intake';

export interface SimulationData {
  country?: string;
  city?: string;
  email?: string;
  _ts?: number;
  [key: string]: string | number | undefined;
}

const VALID_PAGES: PageType[] = ['home', 'founder', 'executive', 'investor', 'comparison', 'about', 'contact', 'intake'];

function SetPasswordModal({ user, onDone }: { user: User; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Minimum 8 characters'); return; }
    setSaving(true);
    try {
      await updatePassword(user, password);
      onDone();
    } catch { setError('Failed to set password. Try again.'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: '#fff', borderRadius: '16px', padding: '40px 32px', maxWidth: '420px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <h2 style={{ fontSize: '22px', fontWeight: 700, color: '#1a2e1f', margin: '0 0 8px' }}>Set your password</h2>
        <p style={{ fontSize: '14px', color: '#6b7c6e', margin: '0 0 28px', lineHeight: 1.6 }}>
          You're signed in. Set a password so you can log in directly next time.
        </p>
        {error && <div style={{ background: '#fef2f2', color: '#dc2626', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>{error}</div>}
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <input type="password" placeholder="New password" value={password} onChange={e => setPassword(e.target.value)} required
            style={{ padding: '12px 14px', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '15px', outline: 'none' }} />
          <input type="password" placeholder="Confirm password" value={confirm} onChange={e => setConfirm(e.target.value)} required
            style={{ padding: '12px 14px', border: '1.5px solid #d1d5db', borderRadius: '8px', fontSize: '15px', outline: 'none' }} />
          <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
            <button type="submit" disabled={saving}
              style={{ flex: 1, padding: '12px', background: '#1a6b3c', color: '#fff', border: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '15px', cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Saving...' : 'Set Password'}
            </button>
            <button type="button" onClick={onDone}
              style={{ padding: '12px 18px', background: '#f3f4f6', color: '#374151', border: 'none', borderRadius: '8px', fontWeight: 500, fontSize: '15px', cursor: 'pointer' }}>
              Skip
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EmailPromptModal({ onSubmit }: { onSubmit: (email: string) => void }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();
    if (!trimmed) return;
    setError('');
    setLoading(true);
    onSubmit(trimmed);
  };

  return (
    <div className="login-overlay">
      <div className="login-dialog" style={{ maxWidth: '760px' }} onClick={e => e.stopPropagation()}>
        <div className="login-left">
          <div className="login-left-inner">
            <div className="login-tag"><span className="login-tag-dot"></span><span className="login-tag-line"></span>Secure Sign-In</div>
            <h1 className="login-heading">One more step.<br /><em>Verify your email.</em></h1>
            <p className="login-desc">For your security, Firebase requires you to confirm the email address that received the sign-in link.</p>
          </div>
          <div>
            <div className="login-quote">
              <div className="login-quote-bar"></div>
              <div>
                <div className="login-quote-text">"This is a one-time step when you open a sign-in link on a different device. Enter your email to continue."</div>
                <div className="login-quote-by">Market Entry Pro · Security</div>
              </div>
            </div>
          </div>
        </div>
        <div className="login-right">
          <div className="login-top-bar"></div>
          <div className="login-right-inner">
            <div className="login-eyebrow">Confirm Identity</div>
            <div className="login-title">Verify your email</div>
            <div className="login-sub">Enter the email address where you received the sign-in link.</div>
            {error && (
              <div className="login-alert error">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <span>{error}</span>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="login-group">
                <label>Email address</label>
                <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@company.com" autoFocus required />
              </div>
              <button type="submit" className={`login-submit ${loading ? 'loading' : ''}`} disabled={loading}>
                {loading
                  ? <div className="login-spinner"></div>
                  : <><span>Continue to your dashboard</span><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg></>
                }
              </button>
            </form>
            <div className="login-foot">
              <div className="login-secure">
                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                Link expires after one use<span className="login-secure-dot"></span>Same email as your simulation request
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [intakeOpen, setIntakeOpen] = useState<boolean>(false);
  const [simulationData, setSimulationData] = useState<SimulationData>({});
  const [currentUser, setCurrentUser] = useState<User | null | undefined>(undefined);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setCurrentUser(u);
      const path = window.location.pathname;
      if (u && (path === '/home' || path === '/login' || path === '/') && !isSignInWithEmailLink(auth, window.location.href)) {
        navigate('/dashboard', { replace: true });
      }
    });
    return unsub;
  }, [navigate]);

  const loginOpen = location.pathname === '/login';
  const pathSegment = location.pathname.slice(1) as PageType;
  const currentPage: PageType = VALID_PAGES.includes(pathSegment) ? pathSegment : 'home';

  const setCurrentPage = (page: PageType) => navigate(`/${page}`);

  const openIntake = (data: SimulationData = {}): void => {
    setIntakeOpen(false);
    if (data.country) {
      localStorage.setItem('lastCountry', data.country);
    }
    setSimulationData({ ...data, _ts: Date.now() });
    navigate('/intake');
  };

  return (
    <div className="App">
      <Navbar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        onLoginClick={() => navigate('/login')}
        onStartSimulation={openIntake}
      />
      <LoginModal isOpen={loginOpen} onClose={() => navigate(-1)} onSuccess={() => navigate('/dashboard', { replace: true })} />
      <IntakeModal
        isOpen={intakeOpen}
        onClose={() => setIntakeOpen(false)}
        onStartSimulation={openIntake}
      />

      <Routes>
        <Route path="/" element={<Navigate to="/home" replace />} />

        <Route path="/home" element={
          <>
            <Hero onStartSimulation={openIntake} />
            <LogosBar />
            <DecisionSection />
            <WhatsIncluded />
            <MultiMarketComparison onStartSimulation={openIntake} />
            <WhySimulation />
            <HowWeBuild />
            <GlobalCoverage />
            <FAQ />
            <BottomCTA onStartSimulation={openIntake} />
            <Footer setCurrentPage={setCurrentPage} />
          </>
        } />

        <Route path="/founder" element={
          <>
            <FounderPage setCurrentPage={setCurrentPage} />
            <BottomCTA
              onStartSimulation={openIntake}
              title="Know before you commit.<br/><em>Start your simulation today.</em>"
              subtitle="Submit your target market and receive a complete 12-month simulation — revenue, costs, break-even timing, and a multi-market comparison."
            />
            <Footer setCurrentPage={setCurrentPage} />
          </>
        } />

        <Route path="/executive" element={
          <>
            <ExecutivePage setCurrentPage={setCurrentPage} />
            <BottomCTA
              onStartSimulation={openIntake}
              title="See the full picture<br/><em>before you commit the budget.</em>"
              subtitle="Submit your target market and receive a complete 12-month simulation — revenue, costs, break-even timing, and a multi-market comparison."
            />
            <Footer setCurrentPage={setCurrentPage} />
          </>
        } />

        <Route path="/investor" element={
          <>
            <InvestorPage setCurrentPage={setCurrentPage} />
            <BottomCTA
              onStartSimulation={openIntake}
              title="De-risk the expansion.<br/><em>Strengthen the investment.</em>"
              subtitle="Submit any target market for any deal or portfolio company. We deliver a complete 12-month simulation with multi-market comparison within 72 hours."
            />
            <Footer setCurrentPage={setCurrentPage} />
          </>
        } />

        <Route path="/comparison" element={
          <>
            <ComparisonPage setCurrentPage={setCurrentPage} onStartSimulation={openIntake} />
            <BottomCTA
              onStartSimulation={openIntake}
              title="See how your market<br/><em>really ranks.</em>"
              subtitle="Submit one target country. We compare it against similar markets and deliver a full ranked analysis within 72 hours."
            />
            <Footer setCurrentPage={setCurrentPage} />
          </>
        } />

        <Route path="/about" element={
          <>
            <AboutPage setCurrentPage={setCurrentPage} />
            <Footer setCurrentPage={setCurrentPage} />
          </>
        } />

        <Route path="/contact" element={
          <>
            <ContactPage />
            <Footer setCurrentPage={setCurrentPage} />
          </>
        } />

        <Route path="/intake" element={
          <IntakePage
            key={simulationData._ts ?? 'init'}
            setCurrentPage={setCurrentPage}
            initialData={simulationData}
          />
        } />

        <Route path="/login" element={null} />

        <Route path="/setup" element={<SetupPage />} />

        <Route path="/dashboard" element={
          currentUser === undefined
            ? <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'sans-serif', color: '#8a8680' }}>Loading...</div>
            : currentUser
              ? <DashboardPage user={currentUser} onNewSimulation={() => { openIntake(); }} />
              : <Navigate to="/home" replace />
        } />
      </Routes>
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
